import React from "react"
import identityStorage from "../../infrastructure/authorization/identityStorage";
import appRoutes from "../../infrastructure/constants/appRoutes";
import userRole from "../../infrastructure/constants/identity/userRole";
import httpClient from "../../infrastructure/helpers/common/httpClient";
import { getFilenameDetails } from "../../infrastructure/helpers/common/pathUtils";
import typeHelper from "../../infrastructure/helpers/common/typeHelper";
import urlHelper from "../../infrastructure/helpers/common/urlHelper";
import { generateKey } from "../../infrastructure/helpers/react/commonHelper";
import ObjectIndexer from "../../infrastructure/types/ObjectIndexer";
import FileWithDetails from "../../infrastructure/types/common/FileWithDetails";
import actions from "../../store/actions";
import store from "../../store/store";
import BusinessAnalysisDocument from "../../types/businessAnalysis/BusinessAnalysisDocument";
import BusinessAnalysisField from "../../types/businessAnalysis/BusinessAnalysisField";
import UploadFileModal from "../Common/Modals/UploadFileModal/UploadFIleModal";
import WarningModal from "../Common/Modals/WarningModal/WarningModal";
import QuickActions from "../Common/QuickActions/QuickActions";
import ReportLogicList from "../Report/Common/ReportLogicList/ReportLogicList";
import { getColumns, getSuperAdminColumns } from "./BusinessAnalysisEditPage.Grid";
import { updateNumbers } from "./BusinessAnalysisEditPage.Helpers";
import history from '../../infrastructure/helpers/react/history';

interface State {
    showFileUploadModal: boolean,
    files: FileWithDetails[],
    isWaiting: boolean,
    showWarningModal: boolean;
    removeHeader: BusinessAnalysisField | null;
    currentDocument: BusinessAnalysisDocument;
    message: string;
    removeFileInField: BusinessAnalysisField | null;
    uploadForFieldNumber: number;
    removeFile: FileWithDetails;
    removeModalTitle: string;
    isUserSuperAdmin: boolean;
    isFieldsUpload: boolean;
}

class BusinessAnalysisEditPage extends React.Component<any, State> {

    private id: number;
    private query: ObjectIndexer<string>;
    private isValidQueryParam: boolean;

    constructor(props: any) {
        super(props);

        this.query = urlHelper.deserializeQuery(history.location.search);

        this.id = parseInt(this.query['id']);

        this.isValidQueryParam = typeHelper.isNumber(this.id);

        if (!this.isValidQueryParam) {
            let route = appRoutes.businessAnalysis.home;

            history.push(route);
        }

        let identity = identityStorage.get();

        this.state = {
            showFileUploadModal: false,
            files: [],
            isWaiting: false,
            currentDocument: { fields: [] as BusinessAnalysisField[] } as BusinessAnalysisDocument,
            removeHeader: {} as BusinessAnalysisField,
            showWarningModal: false,
            message: '',
            removeModalTitle: '',
            removeFile: {} as FileWithDetails,
            removeFileInField: {} as BusinessAnalysisField,
            uploadForFieldNumber: 0,
            isUserSuperAdmin: identity.userRole === userRole.admin,
            isFieldsUpload: false
        }

        this.onFilePicked = this.onFilePicked.bind(this);
        this.exit = this.exit.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);
        this.onRemoveFieldClick = this.onRemoveFieldClick.bind(this);
        this.onRemoveConfirmed = this.onRemoveConfirmed.bind(this);
        this.onNameChange = this.onNameChange.bind(this);
        this.onDescriptionChange = this.onDescriptionChange.bind(this);
        this.onCommentChange = this.onCommentChange.bind(this);
        this.onUploadClick = this.onUploadClick.bind(this);
        this.onAddFieldClick = this.onAddFieldClick.bind(this);
        this.onRemoveFileClick = this.onRemoveFileClick.bind(this);
        this.onVersionManagementClick = this.onVersionManagementClick.bind(this);
        this.onUploadFromFileClick = this.onUploadFromFileClick.bind(this);
    }

    componentDidMount() {
        this.getAndSetDocument();
    }

    async getAndSetDocument() {

        let response = await this.getDocument();

        this.setState({ currentDocument: response.data });
    }

    onUploadClick(fieldNumber: number) {
        this.setState({ showFileUploadModal: true, uploadForFieldNumber: fieldNumber });
    }

    onNameChange(value: string, number: number) {

        let document = structuredClone(this.state.currentDocument);

        let field = document.fields.find(x => x.number === number) as BusinessAnalysisField;

        field.name = value;

        this.setState({ currentDocument: document });
    }

    onDescriptionChange(value: string, number: number) {

        let document = structuredClone(this.state.currentDocument);

        let field = document.fields.find(x => x.number === number) as BusinessAnalysisField;

        field.description = value;

        this.setState({ currentDocument: document });
    }

    onCommentChange(value: string, number: number) {

        let document = structuredClone(this.state.currentDocument);

        let field = document.fields.find(x => x.number === number) as BusinessAnalysisField;

        field.comment = value;

        this.setState({ currentDocument: document });
    }

    onAddFieldClick() {

        let document = structuredClone(this.state.currentDocument);

        document.fields.push({ files: [] as FileWithDetails[], name: '', description: '', comment: '', number: document.fields.length + 1 } as BusinessAnalysisField);

        this.setState({ currentDocument: document });
    }

    getDocument() {
        let url = urlHelper.buildRoute(['business-analysis/document', this.id]);

        return httpClient.get<BusinessAnalysisDocument>(url);
    }

    async proceedWithFields(file: File): Promise<void> {

        var formData = new FormData();
        formData.append('file', file);

        var response = await httpClient.postFile('business-analysis/read/fields', formData);

        let document = structuredClone(this.state.currentDocument);

        document.fields = response.data;

        this.setState({ currentDocument: document, showFileUploadModal: false });
    }

    onFilePicked(files: FileList): void {

        store.dispatch({ type: actions.navigation.setUnsafe, payload: null as any });

        let file = files.item(0) as File;

        if (this.state.isUserSuperAdmin) {

            this.proceedWithFields(file);
        }
        else {

            let details = getFilenameDetails((file ? file.name : null) as string);

            let document = structuredClone(this.state.currentDocument);

            let fieldFiles: FileWithDetails[] = document.fields.find(x => x.number === this.state.uploadForFieldNumber)?.files || [];

            let fileWithDetails = {
                name: details.name,
                extension: details.extension,
                file,
                number: fieldFiles.length + 1,
                businessAnalysisFieldId: 0,
                storageKey: ''
            } as FileWithDetails;

            fieldFiles.push(fileWithDetails);

            this.setState({ currentDocument: document, showFileUploadModal: false });
        }
    }

    prepareDocumentAsFormData(document: BusinessAnalysisDocument) {

        const formData = new FormData();

        formData.append("Id", document.id.toString());
        formData.append("Name", document.name || '');
        formData.append("CreatedBy", document.createdBy.toString());

        // Fields
        document.fields.forEach((field, i) => {
            formData.append(`Fields[${i}].Id`, (field.id || '').toString());
            formData.append(`Fields[${i}].Number`, field.number.toString());
            formData.append(`Fields[${i}].Name`, field.name || '');
            formData.append(`Fields[${i}].Description`, field.description || '');
            formData.append(`Fields[${i}].Comment`, field.comment || '');

            // Files
            field.files.forEach((currentFile, j) => {
                formData.append(`Fields[${i}].Files[${j}].Id`, (currentFile.id || '').toString());
                formData.append(`Fields[${i}].Files[${j}].Name`, currentFile.name || '');
                formData.append(`Fields[${i}].Files[${j}].Number`, currentFile.number.toString());
                formData.append(`Fields[${i}].Files[${j}].Extension`, currentFile.extension || '');
                formData.append(`Fields[${i}].Files[${j}].File`, currentFile.file);
            });
        });

        return formData;
    }

    exit(): void {

        let route = appRoutes.businessAnalysis.home;

        history.push(route);
    }

    async onSaveClick(shouldExit: boolean): Promise<any> {

        this.setState({ isWaiting: true });

        let document = this.prepareDocumentAsFormData(this.state.currentDocument);

        await httpClient.put('business-analysis/document', document);

        await this.getAndSetDocument();

        this.setState({ isWaiting: false });

        const payload = (): void => {

            if (shouldExit) {
                this.exit();
            }
        };

        store.dispatch({ type: actions.navigation.execute, payload: payload });
    }

    onRemoveFieldClick(field: BusinessAnalysisField) {

        let message = `Are you sure you want to delete the document ${field.name}?`;

        this.setState({ removeModalTitle: 'Remove field', message, showWarningModal: true, removeHeader: field });
    }


    onRemoveFileClick(fileWithDetails: FileWithDetails, field: BusinessAnalysisField) {

        let message = `Are you sure you want to delete the file ${fileWithDetails.name}?`;

        this.setState({ removeModalTitle: 'Remove file', message, showWarningModal: true, removeFileInField: field, removeFile: fileWithDetails });
    }

    onRemoveConfirmed() {

        let document = structuredClone(this.state.currentDocument);

        if (this.state.removeHeader && Object.keys(this.state.removeHeader).length > 0) {
            document.fields = document.fields.filter(x => x.id !== this.state.removeHeader?.id);
            updateNumbers(document.fields);
        }
        else if (this.state.removeFileInField) {

            let field = document.fields.find(x => x.number === this.state.removeFileInField?.number) as BusinessAnalysisField;

            field.files = field.files.filter(x => x.number !== this.state.removeFile.number);
            updateNumbers(field.files);
        }

        this.setState({ showWarningModal: false, currentDocument: document, removeFileInField: {} as BusinessAnalysisField, removeHeader: {} as BusinessAnalysisField });
    }

    onVersionManagementClick() {

        history.push(appRoutes.businessAnalysis.changeLog);
    }

    onUploadFromFileClick() {
        this.setState({ showFileUploadModal: true, isFieldsUpload: true });
    }

    render(): JSX.Element {

        return (
            <>
                <h3>Headers</h3>
                {this.state.isUserSuperAdmin ?
                    <div>
                        <div className='display-flex justify-content-between margin-bottom-20px'>
                            <button type="button" className="btn cb-btn" onClick={this.onUploadFromFileClick}>Upload Fields</button>
                            <button type="button" className="btn cb-btn" onClick={this.onVersionManagementClick}>Version Management</button>
                        </div>
                        <ReportLogicList
                            isWaiting={false}
                            items={this.state.currentDocument.fields}
                            columns={getSuperAdminColumns(
                                this.onNameChange,
                                this.onDescriptionChange,
                                this.onRemoveFieldClick
                            )}
                            getContent={() => <></>}
                            getKey={generateKey}
                            createHtmlId={() => generateKey().toString()} />
                    </div>
                    :
                    <ReportLogicList
                        isWaiting={false}
                        items={this.state.currentDocument.fields}
                        columns={getColumns(
                            this.onNameChange,
                            this.onDescriptionChange,
                            this.onCommentChange,
                            this.onUploadClick,
                            this.onRemoveFieldClick,
                            this.onRemoveFileClick
                        )}
                        getContent={() => <></>}
                        getKey={generateKey}
                        createHtmlId={() => generateKey().toString()} />
                }
                <div className="mt-4 mb-4">
                    <button onClick={this.onAddFieldClick} className="btn btn-light">+ Field</button>
                </div>
                {
                    this.state.showWarningModal &&
                    <WarningModal
                        onOkClick={this.onRemoveConfirmed}
                        onCancelClick={() => this.setState({ removeHeader: null, removeFileInField: null, showWarningModal: false })}
                        title={this.state.removeModalTitle}
                        message={this.state.message} />
                }
                <QuickActions
                    isWaiting={this.state.isWaiting}
                    isReadOnly={false}
                    onSaveClick={() => this.onSaveClick(false)}
                    onSaveAndExitClick={() => this.onSaveClick(true)}
                    onExitClick={this.exit}>
                </QuickActions>
                {
                    this.state.showFileUploadModal &&
                    <UploadFileModal
                        onClose={() => this.setState({ showFileUploadModal: false })}
                        onFilePicked={this.onFilePicked}
                        maxFileCount={10}
                        uniqueName="businessAnalysisUpload"
                        maxFileSizeInMb={10}
                        autoReset={false}
                        height={200}
                        allowedExtensions={['pdf', 'xls', 'xlsx', 'csv']}
                    />
                }
            </>
        );
    }
}

export default BusinessAnalysisEditPage;
